import React, { useState } from 'react';
import { Autocomplete, Box, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { getProductionGroupName } from '@paragon/production-app-utilities';
import { assertExhaustiveSwitch, naturalSortByProperty } from '@paragon/utilities';

import { addToScheduleGroup, createScheduleGroup } from '../../../actions/scheduleGroupActions';
import { getBundleGuidFromRowId, getProductionGroupsForCheckedRows, isBundleRow } from '../../../selectors/backlogGrid';
import { getScheduleGroupGuidsForScheduleGrid } from '../../../selectors/scheduleGrid';
import { RootState } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ScheduleGroupProperties } from './ScheduleGroupProperties';

export function CreateOrAddToScheduleGroupDialog() {
  const dispatch = useAppDispatch();
  const checkedRows = useAppSelector((state: RootState) => state.backlogGrid.checkedRows);
  const productionGroupsForCheckedRows = useAppSelector(getProductionGroupsForCheckedRows);
  const scheduleGroupsState = useAppSelector((state) => state.scheduleGroups);
  const scheduleGroupGuidsInScheduleGrid = useAppSelector(getScheduleGroupGuidsForScheduleGrid);
  const scheduleGroupsForScheduleGrid = scheduleGroupGuidsInScheduleGrid.map(
    (scheduleGroupGuid) => scheduleGroupsState[scheduleGroupGuid],
  );

  const [createOrAddTo, setCreateOrAddTo] = useState<'create' | 'add-to'>('create');

  const [scheduleGroupName, setScheduleGroupName] = useState<string | null>(
    productionGroupsForCheckedRows.map((productionGroup) => getProductionGroupName(productionGroup, true)).join(', '),
  );
  const [scheduleLineGroupNameError, setScheduleGroupNameError] = useState<boolean>(false);
  const [targetDate, setTargetDate] = useState<string | null>(null);

  const [selectedScheduleGroupGuid, setSelectedScheduleGroupGuid] = useState<string | null>(null);

  function submit() {
    const bundleGuids = checkedRows.filter(isBundleRow).map((bundleRowId) => getBundleGuidFromRowId(bundleRowId));
    switch (createOrAddTo) {
      case 'create': {
        if (!scheduleGroupName) return;
        dispatch(createScheduleGroup(bundleGuids, scheduleGroupName, targetDate));
        break;
      }
      case 'add-to': {
        dispatch(addToScheduleGroup(selectedScheduleGroupGuid!, bundleGuids));
        break;
      }
      default:
        assertExhaustiveSwitch(createOrAddTo);
    }
    dispatch(closeCurrentPopup());
  }

  let submitText: string;
  switch (createOrAddTo) {
    case 'create': {
      submitText = 'Create';
      break;
    }
    case 'add-to': {
      submitText = 'Add To';
      break;
    }
    default:
      assertExhaustiveSwitch(createOrAddTo);
  }

  let submitDisabled: boolean;
  switch (createOrAddTo) {
    case 'create': {
      submitDisabled = scheduleGroupName === null;
      break;
    }
    case 'add-to': {
      submitDisabled = selectedScheduleGroupGuid === null;
      break;
    }
    default:
      assertExhaustiveSwitch(createOrAddTo);
  }

  return (
    <Dialog
      title="Create/Add to Schedule Group"
      open
      submitText={submitText}
      onSubmit={submit}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '400px', height: '250px' }}
      submitDisabled={submitDisabled}
      enterKeySubmits
    >
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={createOrAddTo}
          exclusive
          onChange={(event, value) => {
            if (value !== null) setCreateOrAddTo(value);
          }}
        >
          <ToggleButton value="create">Create</ToggleButton>
          <ToggleButton value="add-to">Add To</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {createOrAddTo === 'create' && (
        <ScheduleGroupProperties
          scheduleGroupName={scheduleGroupName}
          setScheduleGroupName={(newScheduleGroupName) => {
            setScheduleGroupName(newScheduleGroupName);
            if (newScheduleGroupName === '') setScheduleGroupNameError(true);
          }}
          scheduleGroupNameError={scheduleLineGroupNameError}
          targetDate={targetDate}
          setTargetDate={setTargetDate}
        />
      )}
      {createOrAddTo === 'add-to' && (
        <Autocomplete
          sx={{ mt: 1 }}
          size="small"
          options={naturalSortByProperty(scheduleGroupsForScheduleGrid, (scheduleGroup) => scheduleGroup.name)}
          getOptionLabel={(scheduleGroup) => scheduleGroup.name}
          renderOption={(props, option) => (
            <li {...props} key={option.guid}>
              {option.name}
            </li>
          )}
          renderInput={(params) => <TextField {...params} variant="outlined" label="Select Schedule Group" />}
          value={
            selectedScheduleGroupGuid
              ? (scheduleGroupsForScheduleGrid.find(
                  (scheduleLineGroup) => scheduleLineGroup.guid === selectedScheduleGroupGuid,
                ) ?? null)
              : null
          }
          onChange={(event, scheduleGroup) => void setSelectedScheduleGroupGuid(scheduleGroup?.guid ?? null)}
        />
      )}
    </Dialog>
  );
}
